import { addDoc, collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    StyleSheet,
    FlatList,
    TouchableOpacity,
    SafeAreaView,
    ActivityIndicator,
    TextInput,
    ScrollView,
} from 'react-native';

import { db, auth } from '../firebase/firebase';
import { OrderModel } from '../model/orderModel';
import OrderMapModal from './OrderMapModal';


interface ChatSession {
    id: string;
    userId: string;
    userName: string;
    userEmail: string;
    status: 'active' | 'waiting' | 'closed';
    assignedAgent?: string;
    agentName?: string;
    createdAt: any;
    lastMessageAt: any;
    lastMessage?: string;
    unreadCount: number;
    orderId?: string;
    orderCode?: string;
}

interface Message {
    id: string;
    text: string;
    senderId: string;
    senderName: string;
    senderType: 'user' | 'support' | 'system';
    timestamp: any;
    read: boolean;
    messageType: 'text' | 'image' | 'system';
}

const STATUS_COLOR: Record<string, string> = {
    DELIVERED: '#10b981',
    CANCELLED: '#ef4444',
    CANCEL:    '#ef4444',
    PENDING:   '#f59e0b',
    CONFIRMED: '#3b82f6',
    ACCEPTED:  '#3b82f6',
    PROCESSING:'#8b5cf6',
    PICKED:    '#8b5cf6',
    PREPARING: '#8b5cf6',
};

const AdminDashboard: React.FC = () => {
    const [activeChats, setActiveChats] = useState<ChatSession[]>([]);
    const [waitingChats, setWaitingChats] = useState<ChatSession[]>([]);
    const [selectedChat, setSelectedChat] = useState<ChatSession | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [isTyping, setIsTyping] = useState(false);

    // ── Order search panel ──────────────────────────────────────────
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<OrderModel[]>([]);
    const [defaultOrders, setDefaultOrders] = useState<OrderModel[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchDone, setSearchDone] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<OrderModel | null>(null);
    const [mapOrder, setMapOrder] = useState<OrderModel | null>(null);
    const [deliveryPartnerDetails, setDeliveryPartnerDetails] = useState<{ storename?: string; phone?: string; partnerId?: string } | null>(null);
    const searchTimer = useRef<any>(null);
    const knownOrderIdsRef = useRef<Set<string> | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);

    const playNewOrderSound = () => {
        try {
            const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
            if (!Ctx) return;
            if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
            const ctx = audioCtxRef.current!;
            if (ctx.state === 'suspended') ctx.resume();
            const now = ctx.currentTime;
            // Two short beeps — pleasant "ding-ding" pattern
            [0, 0.18].forEach((offset, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(i === 0 ? 880 : 1175, now + offset);
                gain.gain.setValueAtTime(0.0001, now + offset);
                gain.gain.exponentialRampToValueAtTime(0.35, now + offset + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.15);
                osc.connect(gain).connect(ctx.destination);
                osc.start(now + offset);
                osc.stop(now + offset + 0.17);
            });
        } catch (e) {
            console.warn('Sound playback failed:', e);
        }
    };

    const currentUser = auth.currentUser;
    const agentName = currentUser?.displayName || 'Support Agent';

    useEffect(() => {
        if (!selectedOrder?.deliveryPartnerId) {
            setDeliveryPartnerDetails(null);
            return;
        }
        const fetchPartner = async () => {
            try {
                const partnerRef = doc(db, 'deliveryPartner', selectedOrder.deliveryPartnerId!);
                const snap = await getDoc(partnerRef);
                if (snap.exists()) {
                    const data = snap.data();
                    setDeliveryPartnerDetails({
                        partnerId: snap.id,
                        storename: data.storename,
                        phone: data.phone,
                    });
                } else {
                    setDeliveryPartnerDetails({ partnerId: selectedOrder.deliveryPartnerId });
                }
            } catch (e) {
                setDeliveryPartnerDetails({ partnerId: selectedOrder.deliveryPartnerId });
            }
        };
        fetchPartner();
    }, [selectedOrder?.deliveryPartnerId]);

    useEffect(() => {
        if (!currentUser?.uid) return;

        // Listen to active chats assigned to this agent
        const activeQuery = query(
            collection(db, 'supportChats'),
            // where('assignedAgent', '==', currentUser.uid),
            where('status', '==', 'active'),
            orderBy('lastMessageAt', 'desc')
        );

        const unsubscribeActive = onSnapshot(activeQuery, (snapshot) => {
            if (!snapshot) return;
            const chats: ChatSession[] = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            } as ChatSession));
            setActiveChats(chats);
            setLoading(false);
        });

        // Listen to waiting chats
        const waitingQuery = query(
            collection(db, 'supportChats'),
            where('status', '==', 'waiting'),
            orderBy('createdAt', 'asc'),
            limit(20)
        );

        const unsubscribeWaiting = onSnapshot(waitingQuery, (snapshot) => {
            if (!snapshot) return;
            const chats: ChatSession[] = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            } as ChatSession));
            setWaitingChats(chats);
        });

        return () => {
            unsubscribeActive();
            unsubscribeWaiting();
        };
    }, [currentUser?.uid]);

    useEffect(() => {
        if (!selectedChat?.id) return;

        // Listen to messages
        const messagesQuery = query(
            collection(db, 'supportChats', selectedChat.id, 'messages'),
            orderBy('timestamp', 'desc'),
            limit(50)
        );

        const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
            if (!snapshot) return;
            const msgs: Message[] = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            } as Message));
            setMessages(msgs);
        });

        return () => unsubscribe();
    }, [selectedChat?.id]);

    // Update typing indicator
    useEffect(() => {
        if (!selectedChat?.id) return;

        const timeout = setTimeout(() => {
            if (isTyping) {
                const chatRef = doc(db, 'supportChats', selectedChat.id);
                updateDoc(chatRef, { agentTyping: false });
                setIsTyping(false);
            }
        }, 2000);

        return () => clearTimeout(timeout);
    }, [inputText, isTyping, selectedChat?.id]);

    const handleInputChange = (text: string) => {
        setInputText(text);
        
        if (!isTyping && selectedChat?.id) {
            setIsTyping(true);
            const chatRef = doc(db, 'supportChats', selectedChat.id);
            updateDoc(chatRef, { agentTyping: true });
        }
    };

    const cancelOrder = async (chat: ChatSession) => {
        if (!chat.orderId) {
            alert('No order linked to this chat.');
            return;
        }
        if (!window.confirm('Are you sure you want to cancel this order?')) return;
        try {
            const orderRef = doc(db, 'Orders', chat.orderId);
            await updateDoc(orderRef, { status: 'CANCELLED', active: false });
            alert('Order cancelled successfully.');
        } catch (error) {
            console.error('Error cancelling order:', error);
            alert('Failed to cancel order.');
        }
    };

    const assignChat = async (chat: ChatSession) => {
        try {
            const chatRef = doc(db, 'supportChats', chat.id);
            await updateDoc(chatRef, {
                status: 'active',
                assignedAgent: currentUser?.uid,
                agentName: agentName,
            });

            // Send system message
            const messagesRef = collection(db, 'supportChats', chat.id, 'messages');
            await addDoc(messagesRef, {
                text: `${agentName} has joined the chat`,
                senderId: 'system',
                senderName: 'System',
                senderType: 'system',
                timestamp: serverTimestamp(),
                read: false,
                messageType: 'system',
            });

            setSelectedChat({ ...chat, status: 'active', agentName });
        } catch (error) {
            console.error('Error assigning chat:', error);
            alert('Failed to assign chat');
        }
    };

    const sendMessage = async () => {
        if (!inputText.trim() || !selectedChat?.id || !currentUser) return;

        const messageText = inputText.trim();
        setInputText('');
        setSending(true);

        try {
            // Clear typing indicator
            const chatRef = doc(db, 'supportChats', selectedChat.id);
            await updateDoc(chatRef, { agentTyping: false });
            setIsTyping(false);

            // Add message
            const messagesRef = collection(db, 'supportChats', selectedChat.id, 'messages');
            await addDoc(messagesRef, {
                text: messageText,
                senderId: currentUser.uid,
                senderName: agentName,
                senderType: 'support',
                timestamp: serverTimestamp(),
                read: false,
                messageType: 'text',
            });

            // Update session
            await updateDoc(chatRef, {
                lastMessageAt: serverTimestamp(),
                lastMessage: messageText,
            });
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    const closeChat = async () => {
        if (!selectedChat?.id) return;

        try {
            const chatRef = doc(db, 'supportChats', selectedChat.id);
            await updateDoc(chatRef, {
                status: 'closed',
                closedAt: serverTimestamp(),
            });

            const messagesRef = collection(db, 'supportChats', selectedChat.id, 'messages');
            await addDoc(messagesRef, {
                text: 'Chat closed by agent',
                senderId: 'system',
                senderName: 'System',
                senderType: 'system',
                timestamp: serverTimestamp(),
                read: false,
                messageType: 'system',
            });

            setSelectedChat(null);
        } catch (error) {
            console.error('Error closing chat:', error);
        }
    };

    // ── Order search ────────────────────────────────────────────────
    // Live-listen to recent orders so the panel fills by default AND we can
    // play a sound the moment a new order arrives.
    useEffect(() => {
        setSearchLoading(true);
        const unsub = onSnapshot(
            query(collection(db, 'Orders'), orderBy('createdAt', 'desc'), limit(20)),
            (snap) => {
                const orders = snap.docs.map(d => ({ id: d.id, ...d.data() } as OrderModel));
                const currentIds = new Set(orders.map(o => o.id!).filter(Boolean));

                // First snapshot: seed the "known" set without playing a sound.
                if (knownOrderIdsRef.current === null) {
                    knownOrderIdsRef.current = currentIds;
                } else {
                    const prev = knownOrderIdsRef.current;
                    const hasNew = orders.some(o => o.id && !prev.has(o.id));
                    if (hasNew) playNewOrderSound();
                    knownOrderIdsRef.current = currentIds;
                }

                setDefaultOrders(orders);
                setSearchLoading(false);
            },
            (e) => {
                console.error('Orders listener failed:', e);
                setSearchLoading(false);
            }
        );
        return () => unsub();
    }, []);

    // Auto-fetch the order tied to the selected chat so the right panel
    // jumps straight to the order detail for that conversation.
    useEffect(() => {
        const orderId = selectedChat?.orderId;
        if (!orderId) return;
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'Orders', orderId));
                if (cancelled || !snap.exists()) return;
                const order = { id: snap.id, ...snap.data() } as OrderModel;
                setSelectedOrder(order);
                setSearchQuery('');
                setSearchResults([]);
                setSearchDone(false);
            } catch (e) {
                console.error('Failed to auto-fetch chat order:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedChat?.orderId]);

    const handleSearchChange = (text: string) => {
        setSearchQuery(text);
        setSelectedOrder(null);
        if (!text.trim()) { setSearchResults([]); setSearchDone(false); return; }
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => runSearch(text.trim()), 500);
    };

    const runSearch = async (q: string) => {
        setSearchLoading(true);
        setSearchDone(false);
        try {
            const ordersRef = collection(db, 'Orders');
            // Search by orderNumber array-contains
            const [byNumber, byCustomer] = await Promise.all([
                getDocs(query(ordersRef, where('orderNumber', 'array-contains', q), limit(10))),
                getDocs(query(ordersRef, where('customerId', '==', q), limit(10))),
            ]);
            const seen = new Set<string>();
            const results: OrderModel[] = [];
            [...byNumber.docs, ...byCustomer.docs].forEach(d => {
                if (!seen.has(d.id)) { seen.add(d.id); results.push({ id: d.id, ...d.data() } as OrderModel); }
            });
            setSearchResults(results);
        } catch (e) {
            console.error(e);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
            setSearchDone(true);
        }
    };

    const renderChatItem = ({ item, isWaiting }: { item: ChatSession; isWaiting?: boolean }) => (
        <TouchableOpacity
            style={[
                styles.chatListItem,
                selectedChat?.id === item.id && styles.chatListItemActive,
            ]}
            onPress={() => isWaiting ? assignChat(item) : setSelectedChat(item)}
        >
            <View style={styles.chatListItemLeft}>
                <View style={styles.userAvatar}>
                    {/* <MaterialIcons name="person" size={20} color="#6B7280" /> */}
                </View>
                <View style={styles.chatListItemInfo}>
                    <Text style={styles.chatListItemName}>{item.userName}</Text>
                    <Text style={styles.chatListItemMessage} numberOfLines={1}>
                        {item.lastMessage || 'New conversation'}
                    </Text>
                    {item.orderCode && (
                        <Text style={styles.chatListItemOrder}>Order: {item.orderCode}</Text>
                    )}
                </View>
            </View>
            {isWaiting && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <TouchableOpacity
                        style={styles.assignButton}
                        onPress={() => assignChat(item)}
                    >
                        <Text style={styles.assignButtonText}>Assign</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={styles.cancelOrderButton}
                        onPress={() => cancelOrder(item)}
                    >
                        <Text style={styles.cancelOrderButtonText}>Cancel</Text>
                    </TouchableOpacity>
                </View>
            )}
        </TouchableOpacity>
    );

    const renderMessage = ({ item }: { item: Message }) => {
        const isAgent = item.senderType === 'support';
        const isSystem = item.messageType === 'system';

        if (isSystem) {
            return (
                <View style={styles.systemMessage}>
                    <Text style={styles.systemMessageText}>{item.text}</Text>
                </View>
            );
        }

        return (
            <View style={[styles.message, isAgent ? styles.agentMessage : styles.userMessage]}>
                <View style={[styles.messageBubble, isAgent ? styles.agentBubble : styles.userBubble]}>
                    <Text style={[styles.messageText, isAgent ? styles.agentText : styles.userText]}>
                        {item.text}
                    </Text>
                    <Text style={styles.messageTime}>
                        {item.timestamp?.toDate
                            ? item.timestamp.toDate().toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                              })
                            : ''}
                    </Text>
                </View>
            </View>
        );
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#21808D" />
            </View>
        );
    }

    return (
        <SafeAreaView style={styles.container}>
            <View style={styles.content}>
                {/* Chat List Sidebar */}
                <View style={styles.sidebar}>
                    {/* Waiting Chats */}
                    {waitingChats.length > 0 && (
                        <View>
                            <Text style={styles.sectionTitle}>Waiting ({waitingChats.length})</Text>
                            {waitingChats.map(chat => (
                                <React.Fragment key={chat.id}>
                                    {renderChatItem({ item: chat, isWaiting: true })}
                                </React.Fragment>
                            ))}
                        </View>
                    )}

                    {/* Active Chats */}
                    <Text style={styles.sectionTitle}>My Chats ({activeChats.length})</Text>
                    <FlatList
                        data={activeChats}
                        renderItem={({ item }) => renderChatItem({ item })}
                        keyExtractor={item => item.id}
                        showsVerticalScrollIndicator={false}
                    />
                </View>

                {/* Chat Window */}
                <View style={styles.chatWindow}>
                    {selectedChat ? (
                        <>
                            {/* Chat Header */}
                            <View style={styles.chatHeader}>
                                <View>
                                    <Text style={styles.chatHeaderName}>{selectedChat.userName}</Text>
                                    <Text style={styles.chatHeaderEmail}>{selectedChat.userEmail}</Text>
                                    {selectedChat.orderCode && (
                                        <Text style={styles.chatHeaderOrder}>Order: {selectedChat.orderCode}</Text>
                                    )}
                                </View>
                                <TouchableOpacity onPress={closeChat} style={styles.closeButton}>
                                    <Text style={styles.closeButtonText}>Close Chat</Text>
                                </TouchableOpacity>
                            </View>

                            {/* Messages */}
                            <FlatList
                                data={messages}
                                renderItem={renderMessage}
                                keyExtractor={item => item.id}
                                inverted
                                contentContainerStyle={styles.messagesContainer}
                                showsVerticalScrollIndicator={false}
                            />

                            {/* Input */}
                            <View style={styles.inputContainer}>
                                <TextInput
                                    style={styles.input}
                                    value={inputText}
                                    onChangeText={handleInputChange}
                                    placeholder="Type a message..."
                                    multiline
                                    maxLength={1000}
                                />
                                <TouchableOpacity
                                    style={[styles.sendButton, !inputText.trim() && styles.sendButtonDisabled]}
                                    onPress={sendMessage}
                                    disabled={!inputText.trim() || sending}
                                >
                                    <Text style={styles.sendIcon}>{sending ? '…' : '➤'}</Text>
                                </TouchableOpacity>
                            </View>
                        </>
                    ) : (
                        <View style={styles.emptyChat}>
                            <Text style={{ fontSize: 64 }}>💬</Text>
                            <Text style={styles.emptyChatText}>Select a chat to start</Text>
                            <Text style={{ fontSize: 14, color: '#9CA3AF', marginTop: 8 }}>
                                Choose a conversation from the left sidebar
                            </Text>
                        </View>
                    )}
                </View>

                {/* ── Right Panel: Order Search ── */}
                <View style={styles.rightPanel}>
                    <Text style={styles.rightPanelTitle}>🔍 Order Search</Text>

                    {/* Search input */}
                    <View style={styles.searchBox}>
                        <TextInput
                            style={styles.searchInput}
                            value={searchQuery}
                            onChangeText={handleSearchChange}
                            placeholder="Order # or Customer ID"
                            placeholderTextColor="#9CA3AF"
                            clearButtonMode="while-editing"
                        />
                        {searchLoading && <ActivityIndicator size="small" color="#21808D" style={{ marginLeft: 8 }} />}
                    </View>

                    <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
                        {/* Result list */}
                        {!selectedOrder && searchDone && searchResults.length === 0 && (
                            <Text style={styles.noResult}>No orders found</Text>
                        )}

                        {!selectedOrder && !searchQuery.trim() && defaultOrders.length === 0 && !searchLoading && (
                            <Text style={styles.noResult}>No recent orders</Text>
                        )}

                        {!selectedOrder && !searchQuery.trim() && defaultOrders.length > 0 && (
                            <Text style={[styles.detailSectionLabel, { marginBottom: 6 }]}>
                                RECENT ORDERS
                            </Text>
                        )}

                        {!selectedOrder && (searchQuery.trim() ? searchResults : defaultOrders).map(order => {
                            const num = Array.isArray(order.orderNumber) ? order.orderNumber[0] : order.orderNumber;
                            const statusColor = STATUS_COLOR[order.status?.toUpperCase()] ?? '#94a3b8';
                            return (
                                <TouchableOpacity
                                    key={order.id}
                                    style={styles.resultRow}
                                    onPress={() => setSelectedOrder(order)}
                                >
                                    <View style={{ flex: 1 }}>
                                        <Text style={styles.resultOrderNum} numberOfLines={1}>{num}</Text>
                                        <Text style={styles.resultCustomer} numberOfLines={1}>
                                            {order.userLocation?.name || order.customerId}
                                        </Text>
                                    </View>
                                    <View style={{ alignItems: 'flex-end', gap: 4 }}>
                                        <View style={[styles.statusPill, { backgroundColor: statusColor + '20' }]}>
                                            <Text style={[styles.statusPillText, { color: statusColor }]}>
                                                {order.status}
                                            </Text>
                                        </View>
                                        <Text style={styles.resultAmt}>₹{(order.totalPrice || 0).toFixed(0)}</Text>
                                    </View>
                                </TouchableOpacity>
                            );
                        })}

                        {/* Order detail */}
                        {selectedOrder && (
                            <View>
                                <TouchableOpacity style={styles.backBtn} onPress={() => setSelectedOrder(null)}>
                                    <Text style={styles.backBtnText}>← Back to results</Text>
                                </TouchableOpacity>

                                {/* Status badge */}
                                <View style={styles.detailHeader}>
                                    <View style={{ flex: 1 }}>
                                        <Text style={styles.detailOrderNum}>
                                            {Array.isArray(selectedOrder.orderNumber)
                                                ? selectedOrder.orderNumber.join(', ')
                                                : selectedOrder.orderNumber}
                                        </Text>
                                        <View style={[styles.statusPill, {
                                            backgroundColor: (STATUS_COLOR[selectedOrder.status?.toUpperCase()] ?? '#94a3b8') + '20',
                                            alignSelf: 'flex-start', marginTop: 4,
                                        }]}>
                                            <Text style={[styles.statusPillText, {
                                                color: STATUS_COLOR[selectedOrder.status?.toUpperCase()] ?? '#94a3b8'
                                            }]}>{selectedOrder.status}</Text>
                                        </View>
                                    </View>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <TouchableOpacity
                                            style={styles.mapBtn}
                                            onPress={() => setMapOrder(selectedOrder)}
                                        >
                                            <Text style={styles.mapBtnText}>🗺 Map & Assign</Text>
                                        </TouchableOpacity>
                                        <TouchableOpacity
                                            style={styles.cancelOrderButton}
                                            onPress={async () => {
                                                if (!selectedOrder.id) return;
                                                if (!window.confirm('Are you sure you want to cancel this order?')) return;
                                                try {
                                                    await updateDoc(doc(db, 'Orders', selectedOrder.id), { status: 'CANCELLED', active: false });
                                                    alert('Order cancelled.');
                                                    setSelectedOrder(null);
                                                } catch (e) {
                                                    alert('Failed to cancel order.');
                                                }
                                            }}
                                        >
                                            <Text style={styles.cancelOrderButtonText}>✕ Cancel Order</Text>
                                        </TouchableOpacity>
                                    </View>
                                </View>

                                {/* Customer */}
                                <View style={styles.detailSection}>
                                    <Text style={styles.detailSectionLabel}>CUSTOMER</Text>
                                    <Text style={styles.detailRow}>👤 {selectedOrder.userLocation?.name || '—'}</Text>
                                    <Text style={styles.detailRow}>📞 {selectedOrder.userLocation?.contact || '—'}</Text>
                                    <Text style={styles.detailRow} numberOfLines={2}>
                                        📍 {selectedOrder.userLocation?.address || '—'}
                                    </Text>
                                </View>

                                {/* Payment */}
                                <View style={styles.detailSection}>
                                    <Text style={styles.detailSectionLabel}>PAYMENT</Text>
                                    <Text style={styles.detailRow}>Method: {selectedOrder.payment?.method || '—'}</Text>
                                    <Text style={styles.detailRow}>Status: {selectedOrder.payment?.status || '—'}</Text>
                                    <Text style={styles.detailRow}>Amount: ₹{(selectedOrder.payment?.amount || 0).toFixed(2)}</Text>
                                </View>

                                {/* Charges */}
                                <View style={styles.detailSection}>
                                    <Text style={styles.detailSectionLabel}>CHARGES</Text>
                                    <Text style={styles.detailRow}>Product: ₹{(selectedOrder.totalPrice || 0).toFixed(2)}</Text>
                                    <Text style={styles.detailRow}>Delivery: ₹{(selectedOrder.charges?.deliveryCharge || 0).toFixed(2)}</Text>
                                    <Text style={styles.detailRow}>Packing: ₹{(selectedOrder.charges?.packing || 0).toFixed(2)}</Text>
                                    <Text style={styles.detailRow}>Tax: ₹{(selectedOrder.charges?.tax || 0).toFixed(2)}</Text>
                                    <Text style={styles.detailRow}>Service: ₹{(selectedOrder.charges?.service || 0).toFixed(2)}</Text>
                                    <Text style={[styles.detailRow, { fontWeight: '700', color: '#21808D' }]}>
                                        Total: ₹{(
                                            (selectedOrder.totalPrice || 0) +
                                            (selectedOrder.charges?.deliveryCharge || 0) +
                                            (selectedOrder.charges?.packing || 0) +
                                            (selectedOrder.charges?.tax || 0) +
                                            (selectedOrder.charges?.service || 0)
                                        ).toFixed(2)}
                                    </Text>
                                </View>

                                {/* Items */}
                                {selectedOrder.orderItems?.length > 0 && (
                                    <View style={styles.detailSection}>
                                        <Text style={styles.detailSectionLabel}>ITEMS ({selectedOrder.orderItems.length})</Text>
                                        {selectedOrder.orderItems.map((item, i) => (
                                            <View key={i} style={styles.itemRow}>
                                                <Text style={styles.itemName} numberOfLines={1}>{item.productName}</Text>
                                                <Text style={styles.itemMeta}>×{item.quantity}  ₹{item.price}</Text>
                                            </View>
                                        ))}
                                    </View>
                                )}

                                {/* Delivery partner */}
                                {selectedOrder.deliveryPartnerId && (
                                    <View style={styles.detailSection}>
                                        <Text style={styles.detailSectionLabel}>DELIVERY PARTNER</Text>
                                        {deliveryPartnerDetails?.storename && (
                                            <Text style={styles.detailRow}>🛵 {deliveryPartnerDetails.storename}</Text>
                                        )}
                                        {deliveryPartnerDetails?.phone && (
                                            <Text style={styles.detailRow}>📞 {deliveryPartnerDetails.phone}</Text>
                                        )}
                                        <Text style={[styles.detailRow, { color: '#6B7280', fontSize: 11 }]}>
                                            ID: {deliveryPartnerDetails?.partnerId ?? selectedOrder.deliveryPartnerId}
                                        </Text>
                                    </View>
                                )}

                                {/* Seller */}
                                <View style={styles.detailSection}>
                                    <Text style={styles.detailSectionLabel}>SELLER</Text>
                                    <Text style={styles.detailRow}>🏪 {selectedOrder.sellerLocation?.name || '—'}</Text>
                                    <Text style={styles.detailRow}>📞 {selectedOrder.sellerLocation?.contact || '—'}</Text>
                                </View>
                            </View>
                        )}
                    </ScrollView>
                </View>
            </View>
            <OrderMapModal
                visible={!!mapOrder}
                order={mapOrder}
                onClose={() => setMapOrder(null)}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        backgroundColor: 'white',
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    headerTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: '#111827',
    },
    statsContainer: {
        flexDirection: 'row',
        gap: 20,
    },
    statItem: {
        alignItems: 'center',
    },
    statValue: {
        fontSize: 18,
        fontWeight: '700',
        color: '#21808D',
    },
    statLabel: {
        fontSize: 11,
        color: '#6B7280',
    },
    content: {
        flex: 1,
        flexDirection: 'row',
    },
    sidebar: {
        width: 280,
        backgroundColor: 'white',
        borderRightWidth: 1,
        borderRightColor: '#E5E7EB',
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: '700',
        color: '#6B7280',
        padding: 12,
        backgroundColor: '#F9FAFB',
    },
    chatListItem: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
    },
    chatListItemActive: {
        backgroundColor: '#EEF2FF',
        borderLeftWidth: 3,
        borderLeftColor: '#21808D',
    },
    chatListItemLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        gap: 12,
    },
    userAvatar: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: '#F3F4F6',
        justifyContent: 'center',
        alignItems: 'center',
    },
    chatListItemInfo: {
        flex: 1,
    },
    chatListItemName: {
        fontSize: 14,
        fontWeight: '600',
        color: '#111827',
        marginBottom: 2,
    },
    chatListItemMessage: {
        fontSize: 12,
        color: '#6B7280',
        marginBottom: 2,
    },
    chatListItemOrder: {
        fontSize: 10,
        color: '#21808D',
        fontWeight: '500',
    },
    assignButton: {
        backgroundColor: '#21808D',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 12,
    },
    assignButtonText: {
        fontSize: 12,
        fontWeight: '600',
        color: 'white',
    },
    cancelOrderButton: {
        backgroundColor: '#FEE2E2',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#FECACA',
    },
    cancelOrderButtonText: {
        fontSize: 12,
        fontWeight: '600',
        color: '#DC2626',
    },
    chatWindow: {
        flex: 1,
        backgroundColor: 'white',
    },
    chatHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    chatHeaderName: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111827',
    },
    chatHeaderEmail: {
        fontSize: 13,
        color: '#6B7280',
    },
    chatHeaderOrder: {
        fontSize: 12,
        color: '#21808D',
        marginTop: 2,
    },
    closeButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        padding: 8,
    },
    closeButtonText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#EF4444',
    },
    messagesContainer: {
        padding: 16,
    },
    message: {
        marginBottom: 12,
        maxWidth: '70%',
    },
    agentMessage: {
        alignSelf: 'flex-end',
    },
    userMessage: {
        alignSelf: 'flex-start',
    },
    messageBubble: {
        borderRadius: 16,
        padding: 12,
    },
    agentBubble: {
        backgroundColor: '#21808D',
        borderBottomRightRadius: 4,
    },
    userBubble: {
        backgroundColor: '#F3F4F6',
        borderBottomLeftRadius: 4,
    },
    messageText: {
        fontSize: 14,
        lineHeight: 20,
    },
    agentText: {
        color: 'white',
    },
    userText: {
        color: '#111827',
    },
    messageTime: {
        fontSize: 10,
        color: 'rgba(255, 255, 255, 0.7)',
        marginTop: 4,
    },
    systemMessage: {
        alignItems: 'center',
        marginVertical: 16,
    },
    systemMessageText: {
        fontSize: 12,
        color: '#6B7280',
        backgroundColor: '#F3F4F6',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 12,
    },
    inputContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        padding: 12,
        borderTopWidth: 1,
        borderTopColor: '#E5E7EB',
        gap: 8,
    },
    input: {
        flex: 1,
        backgroundColor: '#F3F4F6',
        borderRadius: 20,
        paddingHorizontal: 16,
        paddingVertical: 10,
        fontSize: 14,
        maxHeight: 100,
    },
    sendButton: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: '#21808D',
        justifyContent: 'center',
        alignItems: 'center',
    },
    sendButtonDisabled: {
        backgroundColor: '#D1D5DB',
    },
    sendIcon: {
        color: 'white',
        fontSize: 16,
        fontWeight: '700',
    },
    emptyChat: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    emptyChatText: {
        fontSize: 16,
        color: '#6B7280',
        marginTop: 12,
    },

    // ── Right Panel ─────────────────────────────────────────────────
    rightPanel: {
        width: 280,
        backgroundColor: '#fff',
        borderLeftWidth: 1,
        borderLeftColor: '#E5E7EB',
        padding: 12,
        flexDirection: 'column',
    },
    rightPanelTitle: {
        fontSize: 13,
        fontWeight: '700',
        color: '#111827',
        marginBottom: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    searchBox: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F3F4F6',
        borderRadius: 10,
        paddingHorizontal: 10,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },
    searchInput: {
        flex: 1,
        height: 38,
        fontSize: 13,
        color: '#111827',
        outlineStyle: 'none',
    } as any,
    noResult: {
        textAlign: 'center',
        fontSize: 13,
        color: '#9CA3AF',
        marginTop: 24,
    },
    resultRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
        borderRadius: 8,
        marginBottom: 2,
    },
    resultOrderNum: {
        fontSize: 13,
        fontWeight: '700',
        color: '#111827',
    },
    resultCustomer: {
        fontSize: 11,
        color: '#6B7280',
        marginTop: 2,
    },
    resultAmt: {
        fontSize: 12,
        fontWeight: '600',
        color: '#111827',
    },
    statusPill: {
        paddingHorizontal: 7,
        paddingVertical: 2,
        borderRadius: 6,
    },
    statusPillText: {
        fontSize: 10,
        fontWeight: '700',
    },
    mapBtn: {
        backgroundColor: '#e8f5e9',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 8,
        alignSelf: 'flex-start',
        marginTop: 2,
    },
    mapBtnText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#16a34a',
    },
    backBtn: {
        marginBottom: 10,
    },
    backBtnText: {
        fontSize: 13,
        color: '#21808D',
        fontWeight: '600',
    },
    detailHeader: {
        marginBottom: 10,
        paddingBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
    },
    detailOrderNum: {
        fontSize: 14,
        fontWeight: '800',
        color: '#111827',
    },
    detailSection: {
        marginBottom: 10,
        paddingBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
    },
    detailSectionLabel: {
        fontSize: 9,
        fontWeight: '700',
        color: '#9CA3AF',
        letterSpacing: 0.8,
        marginBottom: 5,
        textTransform: 'uppercase',
    },
    detailRow: {
        fontSize: 12,
        color: '#374151',
        marginBottom: 3,
        lineHeight: 17,
    },
    itemRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#F9FAFB',
    },
    itemName: {
        fontSize: 12,
        color: '#374151',
        flex: 1,
    },
    itemMeta: {
        fontSize: 11,
        color: '#6B7280',
        fontWeight: '600',
    },
});

export default AdminDashboard;